import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { CommonModule } from '@angular/common';
import { ApiService } from '../../services/api.service';
import { MessageService } from 'primeng/api';
import { environment } from '../../../environments/environment';

@Component({
  standalone: true,
  selector: 'app-oc-proposal-document-upload',
  imports: [CommonModule, ReactiveFormsModule, TableModule, ButtonModule, InputTextModule, TooltipModule],
  templateUrl: './oc-proposal-upload.component.html',
})
export class OcProposalDocumentUploadComponent implements OnInit, OnChanges {
  @Input() vendorId?: string | number;
  @Input() isEdit: boolean = false;
  @Input() url: any = '';
  documentForm!: FormGroup;
  editModeIndex: number | null = null;
  loading: boolean = false;
  baseurl: string = environment.APIUrl;
  constructor(
    private fb: FormBuilder,
    private api: ApiService,
    private messageService: MessageService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    if (this.vendorId) {
      this.fetchDocuments();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['vendorId'] && !changes['vendorId'].firstChange && this.vendorId) {
      this.fetchDocuments();
    }
  }

  initForm(): void {
    this.documentForm = this.fb.group({
      documents: this.fb.array([])
    });
  }

  get documentsArray(): FormArray {
    return this.documentForm.get('documents') as FormArray;
  }

  // GET API - Fetch vendor documents
  fetchDocuments(): void {
    if (!this.vendorId) {
      return;
    }

    this.loading = true;
    let url = `${this.url}?applicant=${this.vendorId}`;
   

    this.api.getAPI(url).subscribe({
      next: (res: any) => {
        this.populateDocuments(res.data || res || []);
        this.loading = false;
      },
      error: (err) => {
        console.error('Error fetching documents:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to fetch vendor documents'
        });
        this.loading = false;
      }
    });
  }

  populateDocuments(docs: any[]): void {
    this.documentsArray.clear();
    docs.forEach((doc) => {
      // Handle file - could be URL string from API or File object
      let fileValue = null;
      if (doc.file) {
        fileValue = doc.file;
      } else if (doc.file_url) {
        fileValue = doc.file_url;
      }

      const documentGroup = this.fb.group({
        id: [doc.id || null],
        documentName: [doc.name || doc.document_name || '', []],
        description: [doc.description || doc.brief_description || '', []],
        file: [fileValue]
      });
      this.documentsArray.push(documentGroup);
    });
  }

  addRow(): void {
    const documentGroup = this.fb.group({
      id: [null],
      documentName: ['', []],
      description: ['', []],
      file: [null],
      isFileSave: [true]
    });
    this.documentsArray.push(documentGroup);
    
  }

  editRow(index: number): void {
    const row = this.documentsArray.at(index) as FormGroup;

    if (this.editModeIndex === index) {
      // Save Mode - PUT API
      this.saveDocument(index);
    } else {
      // Edit Mode
      this.editModeIndex = index;
    }
  }

  // POST API - Create new document
  saveDocument(index: number): void {
    const row = this.documentsArray.at(index) as FormGroup;
    const formData = row.value;

    if (!formData.documentName && !formData.file) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please enter document name or upload a file'
      });
      return;
    }

    this.loading = true;

    const formDataToSend = new FormData();
    formDataToSend.append('name', formData.documentName || '');
    formDataToSend.append('description', formData.description || '');
    
    if (this.vendorId) {
      formDataToSend.append('applicant', String(this.vendorId));
    }


    if (formData.file instanceof File) {
      formDataToSend.append('file', formData.file);
    }

    if (row.get('id')?.value) {
      formDataToSend.append('id', String(row.get('id')?.value));
      formDataToSend.append('action','update' );
      this.updateDocument(index, formDataToSend);
    } else {
      formDataToSend.append('action','create' );
      // POST API - Create new document
      this.api.postAPI(this.url, formDataToSend).subscribe({
        next: (res: any) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Document created successfully'
          });
          this.fetchDocuments();
          this.editModeIndex = null;
          this.loading = false;
        },
        error: (err) => {
          console.error('Error creating document:', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to create document'
          });
          this.loading = false;
        }
      });
    }
  }

  // PUT API - Update existing document
  updateDocument(index: number, formData: FormData): void {
    const row = this.documentsArray.at(index) as FormGroup;
    const documentId = row.get('id')?.value;

    if (!documentId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Document ID not found'
      });
      this.loading = false;
      return;
    }

    this.api.postAPI(`${this.url}`, formData).subscribe({
      next: (res: any) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Document updated successfully'
        });
        this.fetchDocuments();
        this.editModeIndex = null;
        this.loading = false;
      },
      error: (err) => {
        console.error('Error updating document:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update document'
        });
        this.loading = false;
      }
    });
  }

  deleteRow(index: number): void {
    const row = this.documentsArray.at(index) as FormGroup;
    const documentId = row.get('id')?.value;

    if (confirm('Are you sure you want to delete this row?')) {
      if (documentId) {
        // If document exists in API, call POST API with action='delete'
        this.loading = true;
        const formDataToSend = new FormData();
        formDataToSend.append('id', String(documentId));
        formDataToSend.append('action', 'delete');
        
        this.api.postAPI(this.url, formDataToSend).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Document deleted successfully'
            });
            this.fetchDocuments();
            this.loading = false;
          },
          error: (err) => {
            console.error('Error deleting document:', err);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to delete document'
            });
            this.loading = false;
          }
        });
      } else {
        // If it's a new row that wasn't saved, just remove from array
        this.documentsArray.removeAt(index);
        this.messageService.add({
          severity: 'info',
          summary: 'Deleted',
          detail: 'Row removed'
        });
      }
    }
  }

  onFileSelect(event: any, index: number): void {
    const file = event.target.files[0];
    if (file) {
      const row = this.documentsArray.at(index) as FormGroup;
      row.patchValue({ file: file });
    }
  }
}
